import React from "react";
import {
  CAMPAIGN_TOTAL_LABEL,
  CAMPAIGN_ALLOCATION,
  ICANN_OPEN_LABEL,
  ICANN_CLOSE_LABEL,
  ICANN_CLOSE_ISO,
  STEWARD_WIRE_PDF_PATH,
  daysUntilISO,
} from "@shared/campaign";

/**
 * CampaignBanner — the urgency surface. Renders the ICANN-window countdown,
 * the $320K target with full breakdown, and the primary direct-give CTAs:
 * "Direct your gift" anchors to the Wish List, "Wire details (PDF)"
 * downloads the Stewardship Account instructions.
 *
 * Variants:
 *   `hero`   — large, dominant, used on Home above the fold.
 *   `inline` — compact, used on Foundation and Contribute pages.
 */
export function CampaignBanner({ variant = "inline" }: { variant?: "hero" | "inline" }) {
  const daysLeft = Math.max(0, daysUntilISO(ICANN_CLOSE_ISO));

  const Allocation = (
    <ul className="grid grid-cols-1 md:grid-cols-5 gap-4 font-mono text-xs">
      {CAMPAIGN_ALLOCATION.map((line) => (
        <li key={line.short} className="border border-hairline bg-paper p-4 flex flex-col">
          <span className="text-ink font-semibold text-base font-sans">{line.label}</span>
          <span className="text-ink-3 uppercase tracking-widest text-[10px] mt-1">
            {line.short}
          </span>
          <span className="text-ink-2 text-[11px] mt-3 font-sans normal-case tracking-normal leading-relaxed">
            {line.detail}
          </span>
        </li>
      ))}
    </ul>
  );

  if (variant === "hero") {
    return (
      <section
        id="campaign"
        aria-labelledby="campaign-heading"
        className="w-full border-y-2 border-ink bg-paper"
      >
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-14">
          <div className="flex flex-wrap items-baseline gap-4 mb-6">
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-claude font-bold">
              ICANN 2026 Campaign
            </span>
            <span
              className="font-mono text-[10px] uppercase tracking-widest text-ink-3 border border-hairline px-2 py-0.5"
              aria-label={`${daysLeft} days remaining until ICANN window closes`}
            >
              {daysLeft} days until the window closes
            </span>
          </div>
          <h2 id="campaign-heading" className="text-3xl md:text-5xl font-serif text-ink leading-tight mb-6 max-w-3xl">
            Claim <span className="font-mono">.person</span> for personhood — before a registrar claims it for profit.
          </h2>
          <p className="text-ink-2 font-serif text-lg md:text-xl max-w-3xl mb-8 leading-relaxed">
            ICANN's 2026 new top-level domain window — the first since 2012 — opens on{" "}
            <strong className="text-ink">{ICANN_OPEN_LABEL}</strong> and closes on{" "}
            <strong className="text-ink">{ICANN_CLOSE_LABEL}</strong>. The Foundation is raising{" "}
            <strong className="text-ink">{CAMPAIGN_TOTAL_LABEL}</strong> to file the application under
            mission-locked 501(c)(3) stewardship.
          </p>
          {Allocation}
          <div className="mt-10 flex flex-wrap gap-4">
            <a
              href="/foundation/contribute#wishlist"
              className="text-xs uppercase tracking-widest font-semibold bg-ink text-paper px-8 py-4 hover:bg-ink-2 transition-colors"
            >
              Direct your gift
            </a>
            <a
              href={STEWARD_WIRE_PDF_PATH}
              download
              className="text-xs uppercase tracking-widest font-semibold border border-ink text-ink px-8 py-4 hover:bg-paper-2 transition-colors"
            >
              Wire details (PDF)
            </a>
            <a
              href="/foundation#how-the-money-is-used"
              className="text-xs uppercase tracking-widest font-semibold text-ink-2 px-3 py-4 hover:text-ink transition-colors underline decoration-hairline hover:decoration-ink"
            >
              How the money is used
            </a>
          </div>
        </div>
      </section>
    );
  }

  return (
    <aside
      id="campaign"
      aria-labelledby="campaign-heading"
      className="border border-hairline bg-paper-2 p-8 md:p-10"
    >
      <div className="flex flex-wrap items-baseline gap-4 mb-4">
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-claude font-bold">
          ICANN 2026 Campaign
        </span>
        <span className="font-mono text-[10px] uppercase tracking-widest text-ink-3">
          {daysLeft} days until {ICANN_CLOSE_LABEL}
        </span>
      </div>
      <h2 id="campaign-heading" className="text-2xl md:text-3xl font-serif text-ink mb-3 max-w-2xl">
        Raising {CAMPAIGN_TOTAL_LABEL} before the ICANN window closes.
      </h2>
      <p className="text-ink-2 font-serif mb-6 max-w-2xl leading-relaxed">
        To file the application and place <span className="font-mono">.person</span> under
        mission-locked 501(c)(3) stewardship in perpetuity.
      </p>
      {Allocation}
      <div className="mt-8 flex flex-wrap gap-4">
        <a
          href="/foundation/contribute#wishlist"
          className="text-xs uppercase tracking-widest font-semibold bg-ink text-paper px-6 py-3 hover:bg-ink-2 transition-colors"
        >
          Direct your gift
        </a>
        <a
          href={STEWARD_WIRE_PDF_PATH}
          download
          className="text-xs uppercase tracking-widest font-semibold border border-ink text-ink px-6 py-3 hover:bg-paper-2 transition-colors"
        >
          Wire details (PDF)
        </a>
      </div>
    </aside>
  );
}
